import React from 'react';
import Icon from '../../../components/AppIcon';

const TaxSummary = ({ taxCalculations, taxYear, filingStatus }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(amount);
  };

  const getFilingStatusDisplay = (status) => {
    const statusMap = {
      'single': 'Single',
      'married_joint': 'Married Filing Jointly',
      'married_separate': 'Married Filing Separately',
      'head_of_household': 'Head of Household'
    };
    return statusMap?.[status] || status;
  };

  const summaryItems = [
    {
      label: 'Short-Term Capital Gains',
      value: taxCalculations?.shortTermGains || 0,
      description: 'Taxed as ordinary income',
      color: 'text-destructive'
    },
    {
      label: 'Short-Term Capital Losses',
      value: -(taxCalculations?.shortTermLosses || 0),
      description: 'Can offset short-term gains',
      color: 'text-success'
    },
    {
      label: 'Net Short-Term',
      value: taxCalculations?.netShortTerm || 0,
      description: 'Short-term gains minus losses',
      color: (taxCalculations?.netShortTerm || 0) >= 0 ? 'text-destructive' : 'text-success',
      highlight: true
    },
    {
      label: 'Long-Term Capital Gains',
      value: taxCalculations?.longTermGains || 0,
      description: 'Preferential tax rates (0%, 15%, or 20%)',
      color: 'text-warning'
    },
    {
      label: 'Long-Term Capital Losses',
      value: -(taxCalculations?.longTermLosses || 0),
      description: 'Can offset long-term gains',
      color: 'text-success'
    },
    {
      label: 'Net Long-Term',
      value: taxCalculations?.netLongTerm || 0,
      description: 'Long-term gains minus losses',
      color: (taxCalculations?.netLongTerm || 0) >= 0 ? 'text-warning' : 'text-success',
      highlight: true
    }
  ];

  const taxStrategies = [
    {
      title: 'Tax Loss Harvesting',
      description: 'Realize losses to offset gains and reduce tax liability',
      applicable: (taxCalculations?.shortTermGains || 0) + (taxCalculations?.longTermGains || 0) > 0,
      icon: 'TrendingDown'
    },
    {
      title: 'Hold for Long-Term',
      description: 'Hold assets >1 year for preferential tax treatment',
      applicable: true,
      icon: 'Clock'
    },
    {
      title: 'Asset Location',
      description: 'Hold tax-inefficient investments in tax-advantaged accounts',
      applicable: true,
      icon: 'MapPin'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Tax Summary Card */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-card-foreground">Tax Summary</h2>
          <div className="text-right text-sm text-muted-foreground">
            <p>{taxYear} Tax Year</p>
            <p>{getFilingStatusDisplay(filingStatus)}</p>
          </div>
        </div>

        <div className="space-y-3">
          {summaryItems?.map((item, index) => (
            <div 
              key={index}
              className={`flex justify-between items-start p-3 rounded-md ${
                item?.highlight ? 'bg-muted/50 border border-border' : ''
              }`}
            >
              <div className="flex-1">
                <p className={`text-sm font-medium ${item?.highlight ? 'text-card-foreground' : 'text-muted-foreground'}`}>
                  {item?.label}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {item?.description}
                </p>
              </div>
              <div className="text-right">
                <p className={`font-semibold ${item?.color}`}>
                  {formatCurrency(item?.value)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Estimated Tax */}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="bg-primary/10 rounded-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-card-foreground mb-1">Estimated Tax Liability</p>
                <p className="text-xs text-muted-foreground">
                  Preliminary estimate based on current transactions
                </p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-primary">
                  {formatCurrency(taxCalculations?.estimatedTax || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tax Strategies */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Tax Strategies</h3>
        
        <div className="space-y-4">
          {taxStrategies?.map((strategy, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 rounded-md hover:bg-muted/20 transition-standard">
              <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                strategy?.applicable ? 'bg-success/10' : 'bg-muted/50'
              }`}>
                <Icon 
                  name={strategy?.icon} 
                  size={16} 
                  className={strategy?.applicable ? 'text-success' : 'text-muted-foreground'} 
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-card-foreground mb-1">
                  {strategy?.title}
                </p>
                <p className="text-xs text-muted-foreground">
                  {strategy?.description}
                </p>
              </div>
              <div className="flex items-center">
                {strategy?.applicable && (
                  <Icon name="CheckCircle" size={16} className="text-success" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Important Disclaimer */}
        <div className="mt-6 p-4 bg-warning/10 rounded-md">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
            <div>
              <p className="text-sm font-medium text-card-foreground mb-1">Important Disclaimer</p>
              <p className="text-xs text-muted-foreground">
                This is a preliminary tax estimate. Consult with a qualified tax professional 
                for complete tax planning and compliance. Tax laws are complex and subject to change.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxSummary;